import React, { useEffect, useRef, useState } from 'react';

import { OrderingSelectors, OrderingOperations, determineLocationSessions } from 'polygon-ordering';

import getThemeLookup from '../selectors/getThemeLookup';
import getEditLocationAvailable from '../selectors/getEditLocationAvailable';

import combineStyles from '../utils/combineStyles';

import Text from './Text';
import TouchableOpacity from './TouchableOpacity';

import { LOCATION_MODAL_ID } from '../modals/LocationModal';
import { useAppSelector, useAppDispatch } from '../app/hooks';
import { setCurrentModal } from '../thunks/setCurrentModal';
import {
  CATERING_SALE_TYPES,
  DELIVERY_SALE_TYPES,
  SALE_TYPE,
} from '../libs/polygon-ordering/src/constants/saleType';
import { useTranslation } from 'react-i18next';
import { RiArrowDownSLine } from 'react-icons/ri';
import { Dropdown, DropdownMenu } from 'reactstrap';
import { adjustOrder } from '../thunks/adjustOrder';
import { DELIVERY_DETAILS_MODAL_ID } from '../modals/DeliveryDetailsModal';

import getDeviceTypeMobile from '../selectors/getDeviceTypeMobile';
import DropdownContainer from './DropdownContainer';
import { CONTAINER_PROPERTIES, TEXT_PROPERTIES } from '../utils/theme';
import { CATERING_DETAILS_MODAL_ID } from '../modals/CateringDetailsModal';
import { ORDER_TYPE_MODAL_ID } from '../modals/OrderTypeModal';
import { saleTypeSelected } from '../utils/analytics';

const {
  getLocation,
  getSaleType,
  getEnabledSaleTypes,
  getTable,
  getBypassDeliveryDetails,
  getDeliveryAddressString,
  getOrderingWindowPadding,
} = OrderingSelectors;

const { setBypassDeliveryDetails } = OrderingOperations;

const availableSaleTypes = [
  SALE_TYPE.PICKUP,
  SALE_TYPE.DELIVERY,
  SALE_TYPE.DINE_IN,
  SALE_TYPE.TAKE_AWAY,
  SALE_TYPE.TABLE_ORDER,
  SALE_TYPE.CATERING,
  SALE_TYPE.APP_LANE_PICKUP,
];

const MenuLocationDetails: React.FC = () => {
  const { t } = useTranslation();
  const p = useAppSelector(getThemeLookup);
  const location = useAppSelector(getLocation);
  const saleType = useAppSelector(getSaleType);
  const editLocationAvailable = useAppSelector(getEditLocationAvailable);
  const enableMenuLocationDetails = useAppSelector(state => state.config.enableMenuLocationDetails);
  const orderingWindowPadding = useAppSelector(getOrderingWindowPadding);
  const dispatch = useAppDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const enabledSaleTypes = useAppSelector(getEnabledSaleTypes);

  const hasCateringDeliverySaletype = enabledSaleTypes.includes(SALE_TYPE.CATERING_DELIVERY);
  const isCatering = CATERING_SALE_TYPES.includes(saleType);

  const displayableSaleTypes = availableSaleTypes.filter(option =>
    enabledSaleTypes.includes(option),
  );

  const enhancedLocation = {
    ...location,
    ...determineLocationSessions(location, orderingWindowPadding, saleType),
  };

  const filterSaleTypesByLocation = Boolean(location && enhancedLocation);

  // Sale types that are currently available for this location
  const availableSaleTypesTodayNow = filterSaleTypesByLocation
    ? displayableSaleTypes?.filter(
        option => enhancedLocation?.takingOrders?.includes(option) || option === SALE_TYPE.DELIVERY,
      ) || []
    : displayableSaleTypes;

  const remainingSaleTypes = availableSaleTypesTodayNow?.filter(t => {
    if (hasCateringDeliverySaletype && t == SALE_TYPE.CATERING && isCatering) {
      return false;
    }
    return t !== saleType;
  });
  const table = useAppSelector(getTable);
  const currentDrawer = useAppSelector(state => state.currentDrawer);
  const currentModal = useAppSelector(state => state.currentModalId);
  const deviceTypeMobile = useAppSelector(getDeviceTypeMobile);

  const isDelivery = DELIVERY_SALE_TYPES.includes(saleType);
  const deliveryAddress = useAppSelector(getDeliveryAddressString);
  const bypassDeliveryDetails = useAppSelector(getBypassDeliveryDetails);

  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const mousedownHandler = (event: Event) => {
      // close dropdown if click happened outside of the dropdown
      if (menuRef?.current && !menuRef?.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    // listen for clicks on the whole document
    document.addEventListener('mousedown', mousedownHandler);

    return () => {
      // cleanup
      document.removeEventListener('mousedown', mousedownHandler);
    };
  }, [menuRef]);

  useEffect(() => {
    if (currentDrawer || currentModal) {
      setIsOpen(false);
    }
  }, [currentDrawer, currentModal]);

  const desiredDeliveryAddressNotFound = useAppSelector(
    state => state.desiredDeliveryAddressNotFound,
  );
  const deliveryEstimateFailed = useAppSelector(state => state.deliveryEstimateFailed);
  const bypassedDetailsMissingAddress =
    desiredDeliveryAddressNotFound || deliveryEstimateFailed || !deliveryAddress;

  if (!location || !enableMenuLocationDetails) {
    return null;
  }

  const actionRequiredForAddress =
    isDelivery && bypassDeliveryDetails && bypassedDetailsMissingAddress;

  const getLocationDisplayText = () => {
    if (isDelivery && bypassDeliveryDetails) {
      return bypassedDetailsMissingAddress ? 'Enter address' : deliveryAddress;
    }

    return location?.name;
  };

  const getSaleTypeDisplayText = () => {
    if (saleType === SALE_TYPE.DINE_IN || saleType === SALE_TYPE.TABLE_ORDER) {
      return table ? `${t('tableOrder.table')} ${table}` : t('tableOrder.enterTable');
    }

    const cateringSuffix =
      isCatering && hasCateringDeliverySaletype
        ? '- ' + t(`saleTypeChildren.${[saleType]}.name`)
        : '';

    return `${t(`saleType.${[saleType]}.name`)} ${cateringSuffix}`;
  };

  const getButtonText = () => {
    if (saleType === SALE_TYPE.TABLE_ORDER || saleType === SALE_TYPE.DINE_IN) {
      return t('button.menuLocationDetails.change.table');
    }

    if (isDelivery) {
      return t('button.menuLocationDetails.change.deliveryDetails');
    }

    if (isCatering) {
      return t('button.menuLocationDetails.change.cateringDetails');
    }

    return t('button.menuLocationDetails.change.location', {
      saleType: t(`saleType.${saleType}.name`),
    });
  };

  return (
    <div ref={menuRef}>
      <div style={deviceTypeMobile ? styles.mobileContainer : styles.mainContainer}>
        <TouchableOpacity
          onClick={() => {
            setIsOpen(open => !open);
          }}
          style={combineStyles(
            !deviceTypeMobile && styles.buttonContainer,
            editLocationAvailable && !deviceTypeMobile && styles.editable,
            p(deviceTypeMobile ? 'menuLocationDetailsMobile' : 'menuLocationDetails', [
              'backgroundColor',
            ]),
          )}
          disabled={!editLocationAvailable}
          ariaLabel={editLocationAvailable ? 'edit location' : 'location'}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: deviceTypeMobile ? 0 : '0.25em',
            }}
          >
            <Text
              lines={1}
              themeKey="menuLocationDetailsName"
              variant="body2"
              style={combineStyles(
                { textAlign: 'center' },
                p('menuLocationDetailsAddress', TEXT_PROPERTIES),
                deviceTypeMobile && {
                  maxWidth: 180,
                  lineHeight: '100%',
                  maxHeight: '1.5em',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  display: 'block',
                  ...p('menuLocationDetailsAddressMobile', TEXT_PROPERTIES),
                },
                actionRequiredForAddress &&
                  p(
                    deviceTypeMobile
                      ? 'menuLocationDetailsAddressActionMobile'
                      : 'menuLocationDetailsAddressAction',
                    TEXT_PROPERTIES,
                  ),
              )}
            >
              {getLocationDisplayText()}
            </Text>
            <Text
              themeKey="menuLocationDetailsSaleType"
              variant="body"
              style={combineStyles(
                { paddingLeft: 5 },
                deviceTypeMobile && p('menuLocationDetailsSaleTypeMobile', TEXT_PROPERTIES),
              )}
            >
              {getSaleTypeDisplayText()}
              <RiArrowDownSLine
                size={
                  deviceTypeMobile ? p('menuLocationDetailsNameMobile', ['fontSize']).fontSize : 20
                }
              />
            </Text>
          </div>
        </TouchableOpacity>
      </div>

      <DropdownContainer isOpen={isOpen}>
        <Dropdown isOpen={isOpen} toggle={() => {}}>
          <DropdownMenu
            style={combineStyles(styles.dropdownMenu, p('dropdownMenu', CONTAINER_PROPERTIES))}
          >
            {CATERING_SALE_TYPES.includes(saleType) && hasCateringDeliverySaletype && (
              <TouchableOpacity
                style={styles.dropdownMenuOption}
                hoverStyle={p('dropdownMenuOptionHoverStyle', CONTAINER_PROPERTIES)}
                enableHoverEffect
                onClick={() => {
                  dispatch(
                    setCurrentModal({
                      modalId: ORDER_TYPE_MODAL_ID,
                      params: {
                        saleType: SALE_TYPE.CATERING,
                      },
                    }),
                  );
                }}
              >
                <Text>
                  {t('changeSaleTypeChild', {
                    saleType: t(`saleType.${SALE_TYPE.CATERING}.name`),
                  })}
                </Text>
              </TouchableOpacity>
            )}

            <TouchableOpacity
              style={styles.dropdownMenuOption}
              hoverStyle={p('dropdownMenuOptionHoverStyle', CONTAINER_PROPERTIES)}
              enableHoverEffect
              onClick={() => {
                setIsOpen(status => !status);
                if (isDelivery) {
                  dispatch(
                    setCurrentModal({
                      modalId: DELIVERY_DETAILS_MODAL_ID,
                      params: { saleType },
                    }),
                  );
                } else if (saleType === SALE_TYPE.DINE_IN || saleType === SALE_TYPE.TABLE_ORDER) {
                  dispatch(
                    setCurrentModal({
                      modalId: LOCATION_MODAL_ID,
                      params: {
                        saleType,
                        locationId: Number(location?.id),
                        action: () => {
                          dispatch(setCurrentModal(null));
                        },
                      },
                    }),
                  );
                } else if (isCatering) {
                  // If it is catering pickup open catering details
                  dispatch(
                    setCurrentModal({
                      modalId: CATERING_DETAILS_MODAL_ID,
                    }),
                  );
                } else {
                  dispatch(
                    setCurrentModal({
                      modalId: LOCATION_MODAL_ID,
                      params: { saleType },
                    }),
                  );
                }
              }}
            >
              <Text>{getButtonText()}</Text>
            </TouchableOpacity>

            {remainingSaleTypes.length > 0 ? <hr></hr> : null}

            {remainingSaleTypes.map(type => (
              <TouchableOpacity
                style={styles.dropdownMenuOption}
                hoverStyle={p('dropdownMenuOptionHoverStyle', CONTAINER_PROPERTIES)}
                enableHoverEffect
                onClick={async () => {
                  setIsOpen(status => !status);

                  if (type === SALE_TYPE.DELIVERY) {
                    dispatch(
                      setCurrentModal({
                        modalId: DELIVERY_DETAILS_MODAL_ID,
                      }),
                    );
                  } else if (type === SALE_TYPE.DINE_IN || type === SALE_TYPE.TABLE_ORDER) {
                    setBypassDeliveryDetails(false);
                    if (saleType === SALE_TYPE.DINE_IN || saleType === SALE_TYPE.TABLE_ORDER) {
                      saleTypeSelected({ saleType: type, store: location?.name });
                      await dispatch(
                        adjustOrder({
                          saleType: type,
                          locationId: location?.id,
                        }),
                      );
                    } else {
                      dispatch(
                        setCurrentModal({
                          modalId: LOCATION_MODAL_ID,
                          params: {
                            saleType: type,
                            locationId: Number(location?.id),
                            action: async () => {
                              // Putting this in the callback so the analytics logging isn't triggered optimistically
                              saleTypeSelected({ saleType: type, store: location?.name });
                              await dispatch(
                                adjustOrder({
                                  saleType: type,
                                  locationId: location?.id,
                                }),
                              );
                            },
                          },
                        }),
                      );
                    }
                  } else if (type === SALE_TYPE.CATERING) {
                    dispatch(
                      setCurrentModal({
                        modalId: ORDER_TYPE_MODAL_ID,
                        params: {
                          saleType: SALE_TYPE.CATERING,
                        },
                      }),
                    );
                  } else {
                    saleTypeSelected({ saleType: type, store: location?.name });
                    setBypassDeliveryDetails(false);
                    await dispatch(
                      adjustOrder({
                        saleType: type,
                        locationId: location?.id,
                      }),
                    );
                  }
                }}
                key={type}
              >
                <Text>{t(`saleType.${type}.name`)}</Text>
              </TouchableOpacity>
            ))}
          </DropdownMenu>
        </Dropdown>
      </DropdownContainer>
    </div>
  );
};

const styles: Styles = {
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
    padding: '0.5em 40px',
    borderRadius: '50px',
  },
  mobileContainer: {},
  mainContainer: {
    position: 'absolute',
    bottom: 0,
    left: '50%',
    alignItems: 'center',
    textAlign: 'center',
    transform: 'translate(-50%,0)',
  },
  editable: {
    marginBottom: '1.8em',
  },
  editIcon: {
    opacity: 0.9,
    marginLeft: -4,
    marginRight: -4,
  },
  dropdownMenu: {
    transform: 'translate(-50%, 0)',
    padding: '0.35em',
    borderRadius: '10px',
    border: 'none',
    boxShadow: 'rgb(0 0 0 / 16%) 0px 4px 17px -1px, rgb(0 0 0 / 9%) 0px 2px 2px -1px',
    width: 'max-content',
  },
  dropdownMenuOption: {
    textAlign: 'left',
    padding: '0.6em 0.75em',
    borderRadius: '6px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  dropdownMenuOptionHoverStyle: {
    background: '#EBEBEB',
  },
};

export default MenuLocationDetails;
